// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { actionStyles } from "@guestsupply/Overrides/Widgets/MyListsDetails/MyListsDetailsActions";
import Calendar from "@guestsupply/XDL/Icons/Calendar";
import Plus from "@guestsupply/XDL/Icons/Plus";
import Print from "@guestsupply/XDL/Icons/Print";
import Share from "@guestsupply/XDL/Icons/Share";
import { color, xScreen } from "@guestsupply/XDL/Utilities";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Copy from "@insite/mobius/Icons/Copy";
import Edit from "@insite/mobius/Icons/Edit";
import Eye from "@insite/mobius/Icons/Eye";
import Export from "@insite/mobius/Icons/Share";
import Trash2 from "@insite/mobius/Icons/Trash2";
import { css } from "styled-components";

export const createListDetailButtonStyle = (iconSrc: React.ComponentType | string) => {
    return {
        variant: "secondary",
        icon: {
            src: iconSrc,
            position: "right",
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border-color: transparent;
            transition: all 200ms ease-in-out;
            ${IconWrapper} {
                top: 3px;
                width: 12px;
                height: 12px;
                svg {
                    color: ${color("primary")};
                    overflow: visible;
                }
            }
            &:hover {
                ${IconWrapper} svg {
                    color: ${color("common.ui8")};
                }
            }
            &:focus {
                ${IconWrapper} svg {
                    color: ${color("common.ui9")};
                }
            }
        `,
    } as ButtonPresentationProps;
};

actionStyles.wrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        padding: 15px;
        margin-bottom: 8px;
        border: 1px solid ${color("common.border")};
        ${xScreen("lte", "sm")} {
            flex-direction: column;
        }
    `,
};
actionStyles.buttonWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        flex-direction: row-reverse;
        justify-content: flex-start;
        gap: 15px;
        ${xScreen("lte", "sm")} {
            margin-top: 20px;
            flex-direction: column-reverse;
            align-items: flex-start;
        }
    `,
};

actionStyles.listInfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 10px;
        ${xScreen("lte", "sm")} {
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
        }
    `,
};

actionStyles.innerButtonWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
        }
        ${xScreen("lte", "xs")} {
            flex-direction: column;
        }
    `,
};
actionStyles.wideHidden = {
    below: "lg",
    css: css`
        display: flex;
        flex-wrap: nowrap;
        margin-bottom: 12px;
    `,
};
actionStyles.printButton = {
    ...createListDetailButtonStyle(Print),
};
actionStyles.copyButton = {
    ...createListDetailButtonStyle(Copy),
};
actionStyles.exportButton = {
    ...createListDetailButtonStyle(Export),
};
actionStyles.scheduleButton = {
    ...createListDetailButtonStyle(Calendar),
};
actionStyles.hideButton = {
    ...createListDetailButtonStyle(Eye),
};
actionStyles.editButton = {
    ...createListDetailButtonStyle(Edit),
};
actionStyles.shareButton = {
    ...createListDetailButtonStyle(Share),
};
actionStyles.deleteButton = {
    ...createListDetailButtonStyle(Trash2),
};
actionStyles.addTagsButton = {
    ...createListDetailButtonStyle(Plus),
};
actionStyles.addItemButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
        margin-right: 10px;
    `,
    variant: "secondary",
};
actionStyles.removeSelectedButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
    `,
    variant: "secondary",
};
actionStyles.addListButton = {
    css: css`
        width: 100%;
        padding: 0 15px;
        white-space: nowrap;
    `,
};
actionStyles.editListModal = {
    sizeVariant: "small",
};
actionStyles.copyListModal = {
    sizeVariant: "small",
    mergeCss: true,
    cssOverrides: {
        modalContent: css`
            overflow-y: visible;
        `,
    },
};
actionStyles.overflowMenu = {
    cssOverrides: {
        wrapper: css`
            order: 1;
            margin-left: 5px;
            padding-top: 5px;
        `,
    },
};

actionStyles.headerWrapper = {
    css: css`
        max-width: 50%;
        ${xScreen("lte", "sm")} {
            max-width: none;
        }
    `,
};

actionStyles.listActionsWrapper = {
    css: css`
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    `,
};

actionStyles.nameText = {
    size: 30,
    variant: "h1",
    color: "primary",
    css: css`
        display: block;
        margin-bottom: 5px;
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
    `,
};

actionStyles.sharingStatus = {
    statusText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    privateTooltip: {
        iconProps: {
            css: css`
                svg {
                    height: 16px;
                }
            `,
        },
    },
};

actionStyles.lastUpdateText = {
    as: "p",
    size: 14,
    italic: true,
    weight: "bold",
    color: "common.ui5",
    css: css`
        margin-bottom: 0;
    `,
};

actionStyles.totalWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        margin-right: 20px;
        gap: 5px;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
        }
    `,
};

actionStyles.totalLabel = {
    as: "p",
    size: 22,
    weight: "bold",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.totalText = {
    as: "p",
    size: 22,
    weight: "bold",
    color: "primary",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.totalVatLabelText = {
    size: 12,
    css: css`
        margin-left: 5px;
    `,
};

actionStyles.totalWithoutVatText = {
    weight: "bold",
    css: css`
        margin-left: 5px;
    `,
};

actionStyles.totalWithoutVatLabelText = {
    size: 12,
    css: css`
        margin-left: 5px;
    `,
};

actionStyles.wishlistCountWrapper = {
    css: css`
        display: flex;
        gap: 5px;
        margin-right: 20px;
    `,
};

actionStyles.wishlistLabel = {
    size: 22,
    weight: "bold",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.wishlistCount = {
    size: 22,
    weight: "bold",
    color: "primary",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.detailsWrapper = {
    css: css`
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 10px;

        ${xScreen("lte", "md")} {
            flex-direction: column;
            align-items: flex-start;
        }
    `,
};

actionStyles.tagsWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    `,
};

actionStyles.tagsTitle = {
    variant: "h6",
    css: css`
        margin: 10px 0;
    `,
};

actionStyles.printListModal = {
    modal: {
        sizeVariant: "large",
    },
    submitButton: {
        color: "primary",
    },
    productPropertiesCheckboxGridItem: {
        css: css`
            flex-wrap: wrap;
        `,
    },
};
